import * as XLSX from 'xlsx';
import { parseWorkbook } from '@/components/dataImport/parseWorkbook';

export type ImeiMacRow = { imei: string; mac_address: string };

function pickColumn(row: Record<string, string>, aliases: string[]): string {
  const keys = Object.keys(row);
  for (const alias of aliases) {
    const found = keys.find((k) => k.trim().toLowerCase() === alias.toLowerCase());
    if (found) return (row[found] || '').trim();
  }
  // Positional fallback: first col IMEI, second MAC when headers are weird
  return '';
}

/** Parse Excel/CSV with IMEI + optional MAC columns into serial rows. */
export async function parseImeiMacFile(file: File): Promise<ImeiMacRow[]> {
  const parsed = await parseWorkbook(file);
  if (!parsed.rows.length) {
    throw new Error('No data rows found in the file');
  }

  const imeiAliases = ['imei', 'imei / serial', 'serial', 'serial no', 'serial_no'];
  const macAliases = ['mac', 'mac address', 'mac_address', 'macaddress'];

  const headerLower = parsed.headers.map((h) => h.trim().toLowerCase());
  const hasImeiHeader = headerLower.some((h) => imeiAliases.includes(h));
  if (!hasImeiHeader && parsed.headers.length < 1) {
    throw new Error('File must include an IMEI column header');
  }

  const out: ImeiMacRow[] = [];
  const seen = new Set<string>();

  for (const row of parsed.rows) {
    let imei = pickColumn(row, imeiAliases);
    let mac = pickColumn(row, macAliases);
    if (!imei && parsed.headers.length >= 1) {
      // Fallback: column A = IMEI, column B = MAC
      imei = (row[parsed.headers[0]] || '').trim();
      if (parsed.headers[1]) mac = (row[parsed.headers[1]] || '').trim();
    }
    if (!imei) continue;
    if (seen.has(imei)) {
      throw new Error(`Duplicate IMEI in file: ${imei}`);
    }
    seen.add(imei);
    out.push({ imei, mac_address: mac });
  }

  if (!out.length) {
    throw new Error('No IMEI values found in the file');
  }
  return out;
}

export function downloadImeiMacTemplate() {
  const ws = XLSX.utils.aoa_to_sheet([
    ['IMEI', 'MAC'],
    ['356938035643809', 'AA:BB:CC:DD:EE:FF'],
    ['356938035643810', ''],
  ]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'IMEIs');
  XLSX.writeFile(wb, 'imei_upload_template.xlsx');
}
